import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, ExternalLink, BookOpen, FileText, FolderOpen } from 'lucide-react';

const TEACHING_MATERIALS_URL = "https://drive.google.com/drive/folders/1fUFL4JJLS1zTAZdwBKIMMtsOCDOaPHrg?usp=drive_link";

const TeachingMaterialsContent = () => {
  useEffect(() => {
    // Auto-redirect to Google Drive after showing the page briefly
    const timer = setTimeout(() => {
      window.open(TEACHING_MATERIALS_URL, '_blank');
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8 pt-20">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] mb-6"
        >
          <ArrowLeft size={20} />
          Back to Home
        </Link>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="text-center bg-gradient-to-r from-[#5f7c8a]/10 to-transparent">
            <div className="mx-auto w-16 h-16 bg-[#5f7c8a] rounded-full flex items-center justify-center mb-4">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-semibold text-gray-800">
              Teaching Materials
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              Access teaching resources, lesson plans, and educational materials
            </CardDescription>
          </CardHeader>
          
          <CardContent className="py-8">
            <div className="text-center space-y-6">
              {/* Info */}
              <div className="bg-blue-50 rounded-lg p-4 text-blue-700 text-sm">
                <p>Opening Google Drive in a new tab...</p>
                <p className="text-xs mt-1 text-blue-500">If it doesn't open automatically, click the button below.</p>
              </div>

              {/* Manual Button */}
              <a 
                href={TEACHING_MATERIALS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  className="bg-[#5f7c8a] hover:bg-[#4a6470] gap-2"
                  size="lg"
                  data-testid="open-materials-btn"
                >
                  <FolderOpen size={18} />
                  Open Teaching Materials
                  <ExternalLink size={16} />
                </Button>
              </a>

              {/* Resource Categories */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 pt-6 border-t">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <FileText className="mx-auto text-[#5f7c8a] mb-2" size={24} />
                  <p className="font-medium text-gray-700">Lesson Plans</p>
                  <p className="text-xs text-gray-500">Structured teaching guides</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <BookOpen className="mx-auto text-[#5f7c8a] mb-2" size={24} />
                  <p className="font-medium text-gray-700">Study Materials</p>
                  <p className="text-xs text-gray-500">Educational resources</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <FolderOpen className="mx-auto text-[#5f7c8a] mb-2" size={24} />
                  <p className="font-medium text-gray-700">Reference Docs</p>
                  <p className="text-xs text-gray-500">Supporting documents</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

const TeachingMaterialsPage = () => {
  return (
    <AdminProtected title="Teaching Materials">
      <TeachingMaterialsContent />
    </AdminProtected>
  );
};

export default TeachingMaterialsPage;
